/**
 * @file 图片处理相关 utils
 */
import { htmlToCanvas } from './dom';
import { downloadFile, getExtName } from './file';
/**
 * 将 base64 图片转为 Blob, 然后可用于下载等用途
 *
 * @category Image
 * @param data - base64 字符串
 * @returns 转换后的 Blob
 */
export function base64ImageToBlob(data) {
    const imageTypeExp = /data:(image\/.+);base64,/;
    let mimeString = '';
    let raw = data.replace(imageTypeExp, (_header, imageType) => {
        mimeString = imageType;
        return '';
    });
    raw = atob(raw);
    const rawLength = raw.length;
    let uInt8Array = new Uint8Array(rawLength); // eslint-disable-line
    for (let i = 0; i < rawLength; i += 1) {
        uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: mimeString });
}
/**
 * 将指定 html 元素打印并下载为 jpeg 格式的图片.
 * 内置了一些预处理逻辑:
 * 1. 目标元素内带有 data-html-to-canvas-remove 属性的元素都会在打印前被移除
 *
 * @category Image
 * @param args - Argument object
 * @param args.el - 目标元素
 * @param args.name - 下载的文件名, 应为 xx.jpg
 * @param args.preprocess - 打印前的预处理, 一般用于对打印样式进行调整 (在 clone 的元素中进行, 不影响原元素)
 */
export async function downloadHtmlAsImage({ el, name, preprocess, }) {
    // scale 以增强清晰度
    const canvas = await htmlToCanvas({ el, preprocess, scale: 3 });
    const dataURL = canvas.toDataURL('image/jpeg', 0.9);
    downloadFile(name, dataURL);
}
/**
 * 判断文件名或 URL 是否是图片
 *
 * @category Image
 * @param filenameOrURL - 文件名或 URL
 * @returns 是否
 */
export const isImage = (filenameOrURL) => {
    const ext = getExtName(filenameOrURL);
    return /(webp|svg|png|gif|jpg|jpeg|jfif|bmp|dpg|heic)$/i.test(ext);
};
/**
 * 下载图片
 *
 * @category Image
 * @param imageUrl - 图片地址
 * @param imageName - 下载后的图片名称
 */
export function downloadImage(imageUrl, imageName) {
    const MIME_TYPE = 'image/png';
    const dlLink = document.createElement('a');
    dlLink.download = imageName;
    dlLink.href = imageUrl;
    dlLink.dataset.downloadurl = [MIME_TYPE, dlLink.download, dlLink.href].join(':');
    document.body.appendChild(dlLink);
    dlLink.click();
    document.body.removeChild(dlLink);
}
//# sourceMappingURL=data:application/json;base64,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